/**
 * useUnreadConversationsCount — the Messages tab-badge count.
 *
 * R5 (auth-debt round 2): the aggregate unread count was previously folded
 * into `/api/auth/me`, coupling the Messages badge to auth-state hydration
 * (and letting a conversations RPC failure surface as an auth-hydration
 * failure). It is now its OWN query:
 * - `enabled` only while authenticated, so a signed-out session never fires
 *   the RPC (it also structurally reads 0 — the fallback below);
 * - reactive by construction: signing in flips `isAuthenticated` → the query
 *   fetches; logging out disables it → the badge returns to 0. No manual
 *   "reset to 0 on sign-in / re-hydrate on next /me" bookkeeping.
 *
 * Lives in `shared/` (not `features/conversations/`) because its consumers are
 * the shared layout chrome (MobileBottomNav, AccountMenu), which sit BELOW
 * features in the app DAG.
 */
import { bffGet } from '@/shared/lib/bff';
import { queryKeys } from '@/shared/lib/query-keys';
import { useAuth } from '@/shared/providers/AuthProvider';
import { useQuery } from '@tanstack/react-query';

/** Refetch cadence for the badge — matches the low urgency of a tab badge. */
const UNREAD_COUNT_STALE_MS = 60_000;

export function useUnreadConversationsCount(): number {
  const { isAuthenticated } = useAuth();

  const { data } = useQuery({
    queryKey: queryKeys.conversations.unreadCount,
    queryFn: async () => (await bffGet('/api/conversations/unread-count')).unreadConversationsCount,
    enabled: isAuthenticated,
    staleTime: UNREAD_COUNT_STALE_MS,
  });

  // Signed-out / loading / not-yet-fetched all read as no badge.
  return isAuthenticated ? (data ?? 0) : 0;
}
